import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import L from "leaflet"

// The reference map: CARTO light tiles and one circle marker per pinned
// region, coloured by climate and sized by whether it is in the filtered
// set. Clicking a pin visits the screen with that region selected; the
// server renders the panel, and this controller redraws on connect. The
// map's last zoom is kept across visits so panning in to a region does not
// jump back out on every click, as the legacy single-page map behaved.
//
// Values: regions (id, name, lat, lng, climate, matching), selection
// ({kind, id} or {}), fit ({kind: world|filtered|region|country, ...}),
// filtering (boolean), regionUrl (the region URL with REGION_ID in it),
// cartoApiKey (CARTO now watermarks unauthenticated raster tiles; the key
// is a browser-embedded value like the Honeybadger JS key, not a secret,
// sourced from ENV["CARTO_API_KEY"] and rendered into this data attribute).

const WORLD_CENTER = [25, 10]
const WORLD_ZOOM = 2
const MIN_FILTER_ZOOM = 3
const CLIMATE_COLORS = { cool: "#2563eb", moderate: "#ea580c", warm: "#dc2626" }
const STROKE = "#1c1c1e"
// CARTO's own docs (carto.com/basemaps/apikey): the free raster tiles moved
// under /rastertiles/ and now require ?key=; the bare /light_all/ path this
// used to be is what shows the "API KEY REQUIRED" watermark.
const TILE_URL_TEMPLATE = "https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png"
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'

let lastZoom = null

export default class extends Controller {
  static targets = ["map", "panel"]
  static values = {
    regions: Array,
    selection: Object,
    fit: Object,
    filtering: Boolean,
    regionUrl: String,
    cartoApiKey: String
  }

  connect() {
    this.map = L.map(this.mapTarget, {
      center: WORLD_CENTER,
      zoom: WORLD_ZOOM,
      worldCopyJump: true,
      scrollWheelZoom: true
    })
    const tileUrl = this.cartoApiKeyValue
      ? `${TILE_URL_TEMPLATE}?key=${encodeURIComponent(this.cartoApiKeyValue)}`
      : TILE_URL_TEMPLATE
    L.tileLayer(tileUrl, { subdomains: "abcd", maxZoom: 18, attribution: TILE_ATTRIBUTION }).addTo(this.map)
    this.drawPins()
    this.fit()
    this.scrollPanelIntoView()
  }

  disconnect() {
    if (!this.map) return
    lastZoom = this.map.getZoom()
    this.map.remove()
    this.map = null
  }

  drawPins() {
    const filtering = this.filteringValue
    for (const region of this.regionsValue) {
      const matches = !filtering || region.matching
      const radius = !filtering ? 6 : matches ? 9 : 4
      const opacity = !filtering ? 0.88 : matches ? 1 : 0.22
      const weight = matches ? 2 : 1.5
      const marker = L.circleMarker([region.lat, region.lng], {
        radius,
        color: STROKE,
        weight,
        fillColor: CLIMATE_COLORS[region.climate] || CLIMATE_COLORS.moderate,
        fillOpacity: opacity,
        opacity
      })
      marker.bindTooltip(region.name, { direction: "top" })
      marker.on("click", () => this.visitRegion(region.id))
      marker.addTo(this.map)
    }
  }

  visitRegion(id) {
    Turbo.visit(this.regionUrlValue.replace("REGION_ID", encodeURIComponent(id)))
  }

  // Country selection wins: the whole country's extent, even under a grape
  // filter. Then a region's own pin, then the world when nothing is
  // filtered, then the filtered set.
  fit() {
    const fit = this.fitValue
    const currentZoom = lastZoom ?? this.map.getZoom()
    if (fit.kind === "country") {
      if (fit.points && fit.points.length > 1) {
        this.map.fitBounds(fit.points, { padding: [40, 40], maxZoom: 7 })
        return
      }
      if (fit.center) {
        this.map.setView(fit.center, 5)
        return
      }
    }
    if (fit.kind === "region" && fit.center) {
      this.map.setView(fit.center, Math.max(currentZoom, 6))
      return
    }
    if (!this.filteringValue) {
      this.map.setView(WORLD_CENTER, WORLD_ZOOM)
      return
    }
    const coords = this.regionsValue.filter((region) => region.matching).map((region) => [region.lat, region.lng])
    if (coords.length === 0) return
    if (coords.length === 1) {
      this.map.setView(coords[0], Math.max(currentZoom, 6))
      return
    }
    this.map.fitBounds(coords, { padding: [40, 40], maxZoom: 8 })
    if (this.map.getZoom() < MIN_FILTER_ZOOM) this.map.setZoom(MIN_FILTER_ZOOM)
  }

  // On a phone the panel sits under the map, so a tap on a chip or a pin
  // scrolls it into view rather than looking like nothing happened.
  scrollPanelIntoView() {
    if (!this.hasPanelTarget) return
    if (Object.keys(this.selectionValue).length === 0) return
    if (!window.matchMedia("(max-width: 767px)").matches) return
    this.panelTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }
}
